/**
 * Public PWA base URL for share links from the admin OS (job board, upcoming trips, etc.).
 * Baked at build time via VITE_PWA_PUBLIC_URL (see deploy-cli / Dockerfile.admin).
 *
 * Production must set VITE_PWA_PUBLIC_URL (or DEPLOY_PWA_PUBLIC_URL / PWA_ORIGIN at image
 * build). Loopback hosts are never used outside Vite DEV — that was shipping localhost
 * links when a mis-set build arg leaked into prod.
 */

const LOOPBACK_HOST = /^(localhost|127\.0\.0\.1|\[::1\])$/i;

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isLoopbackPublicUrl(value) {
  if (!value || typeof value !== "string") return false;
  try {
    const host = new URL(value).hostname;
    return LOOPBACK_HOST.test(host);
  } catch {
    return false;
  }
}

/**
 * Pure resolver (testable). Prefer getPwaPublicBaseUrl() at call sites.
 *
 * @param {{ fromEnv?: unknown, isDev?: boolean, windowOrigin?: string | null }} [opts]
 * @returns {string | null}
 */
export function resolvePwaPublicBaseUrl({
  fromEnv,
  isDev = false,
  windowOrigin = null,
} = {}) {
  if (fromEnv && typeof fromEnv === "string") {
    const trimmed = fromEnv.trim().replace(/\/$/, "");
    if (!trimmed) {
      // fall through
    } else if (!isDev && isLoopbackPublicUrl(trimmed)) {
      return null;
    } else if (trimmed) {
      return trimmed;
    }
  }
  // Local admin → local PWA only while running Vite DEV
  if (isDev && windowOrigin) {
    return String(windowOrigin).replace(/:\d+$/, ":5173");
  }
  return null;
}

/**
 * @returns {string | null} Absolute PWA origin without trailing slash, or null
 */
export function getPwaPublicBaseUrl() {
  return resolvePwaPublicBaseUrl({
    fromEnv: import.meta.env.VITE_PWA_PUBLIC_URL,
    isDev: Boolean(import.meta.env.DEV),
    windowOrigin:
      typeof window !== "undefined" ? window.location.origin : null,
  });
}
